import Section from '@pelckmans/elementis/components/section';
import { Cell } from '@pelckmans/elementis/components/table';
import { useTranslation } from 'react-i18next';

import useAssignmentsTeacher from '../hooks/useAssignmentsTeacher';
import { TeacherAssignment } from '../api/AssignmentTeacherApi';
import { Group } from '../../../api/groups';
import GroupsEmptyState from './GroupsEmptyState';
import AssignmentDivision from './AssignmentDivision';
import { splitAssignmentsByEndDateCategory, splitAssignmentsByPastEndDateCategory } from '../utils';
import LoadingState from '../../../shared-components/LoadingState';
import { useContext } from 'react';
import ModuleContext from '../../material/_contexts/ModuleContext';
import NoLicenseState from './NoLicenseState';
import NoAssignmentsState from './NoAssignmentsState';

const BASE = 'PAGES.ASSIGNMENTS.SECTION_PLANNED_AND_ONGOING';

function AssignmentsBody({ assignments }: { assignments: TeacherAssignment[] }) {
  const { assignmentsEndIn24hours, assignmentsEndIn7Days, assignmentsEndOver7Days } =
    splitAssignmentsByEndDateCategory(assignments);

  return (
    <>
      <AssignmentDivision endDateCategory="TODAY" assigments={assignmentsEndIn24hours} />
      <AssignmentDivision endDateCategory="THIS_WEEK" assigments={assignmentsEndIn7Days} />
      <AssignmentDivision endDateCategory="LATER" assigments={assignmentsEndOver7Days} />
    </>
  );
}

function ArchivedAssignmentsBody({ assignments }: { assignments: TeacherAssignment[] }) {
  const {
    i18n: { language },
  } = useTranslation();

  const { endedThisWeek, endedLastWeek, endedByMonth } = splitAssignmentsByPastEndDateCategory(assignments, language);

  return (
    <>
      <AssignmentDivision endDateCategory="LAST_WEEK" assigments={endedThisWeek} />
      <AssignmentDivision endDateCategory="LAST_TWO_WEEKS" assigments={endedLastWeek} />
      {Object.entries(endedByMonth).map(([key, value]) => (
        <AssignmentDivision key={key} endDateCategory="IN_LAST_MONTH" assigments={value} monthAndYear={key} />
      ))}
    </>
  );
}

type AssignmentsTableProps = {
  assignments: TeacherAssignment[];
  archived?: boolean;
};

function AssignmentsTable({ assignments, archived }: AssignmentsTableProps) {
  const { t } = useTranslation();

  return (
    <table className="assignments-table" data-testid="assignments-table">
      <thead>
        <tr>
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.MATERIAL`)} />
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.SECTION`)} />
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.PERIOD`)} />
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.GROUP`)} />
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.STUDENTS`)} />
          <Cell.Header />
        </tr>
      </thead>
      <tbody>
        {archived ? (
          <ArchivedAssignmentsBody assignments={assignments} />
        ) : (
          <AssignmentsBody assignments={assignments} />
        )}
      </tbody>
    </table>
  );
}

interface Props {
  isLoadingGroups: boolean;
  groups: Group[];
  archived?: boolean;
}

function Assignments({ isLoadingGroups, groups, archived }: Props) {
  const { t } = useTranslation();
  const { module } = useContext(ModuleContext);
  const hasDigitalManualLicense = module?.licenses.hasDigitalManualLicenseForTeacher;

  const { assignments, isLoading: isLoadingAssignments } = useAssignmentsTeacher({
    archived,
    disabled: !hasDigitalManualLicense,
  });
  const isLoading = isLoadingGroups || isLoadingAssignments;

  let content;

  if (isLoading) {
    content = <LoadingState variant="table" aria-label={t(`${BASE}.TABLE.EMPTY_STATE.A11Y_LABEL`)} />;
  } else if (!isLoadingGroups && groups.length === 0) {
    content = <GroupsEmptyState />;
  } else if (!hasDigitalManualLicense) {
    content = <NoLicenseState />;
  } else if (!isLoadingAssignments && assignments.length === 0) {
    content = <NoAssignmentsState archived={archived} />;
  } else {
    content = <AssignmentsTable assignments={assignments} archived={archived} />;
  }

  return <Section title={t(`${BASE}.${archived ? 'TITLE_ARCHIVED' : 'TITLE'}`)}>{content}</Section>;
}

export default Assignments;
